import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Card, CardContent, Typography, Box, Chip, Grid, LinearProgress,
  Alert, Rating, CardMedia,
} from '@mui/material';
import {
  Search as SearchIcon,
  Place as PlaceIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { restaurantsApi } from '../api/services';
import type { RestaurantResult } from '../types/models';

interface RestaurantSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (restaurant: RestaurantResult) => void;
}

const PRICE_LABELS: Record<number, string> = {
  1: '\u00A3',
  2: '\u00A3\u00A3',
  3: '\u00A3\u00A3\u00A3',
  4: '\u00A3\u00A3\u00A3\u00A3',
};

export default function RestaurantSearchDialog({ open, onClose, onSelect }: RestaurantSearchDialogProps) {
  const [location, setLocation] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [results, setResults] = useState<RestaurantResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!location.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const data = await restaurantsApi.search(location.trim(), cuisine.trim() || undefined);
      setResults(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to search restaurants');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (restaurant: RestaurantResult) => {
    onSelect(restaurant);
    onClose();
    // Reset state for next open
    setResults([]);
    setSearched(false);
    setLocation('');
    setCuisine('');
  };

  const handleClose = () => {
    onClose();
    setResults([]);
    setSearched(false);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Book Restaurant</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Canary Wharf, Mayfair"
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <TextField
              label="Cuisine (optional)"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
              placeholder="e.g. Italian, Japanese"
              sx={{ minWidth: 200 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading || !location.trim()}
              sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
            >
              Search
            </Button>
          </Stack>

          {loading && <LinearProgress />}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {searched && !loading && results.length === 0 && !error && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PlaceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">No restaurants found for this search</Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {results.map((restaurant) => (
              <Grid size={{ xs: 12, sm: 6 }} key={restaurant.placeId}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}>
                  {restaurant.photoUrl && (
                    <CardMedia
                      component="img"
                      height="120"
                      image={restaurant.photoUrl}
                      alt={restaurant.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ flex: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary', mb: 0.5 }}>
                      {restaurant.name}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      {restaurant.rating !== null && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Rating value={restaurant.rating} precision={0.1} size="small" readOnly />
                          <Typography variant="caption" color="text.secondary">
                            {restaurant.rating}
                          </Typography>
                          {restaurant.userRatingsTotal !== null && (
                            <Typography variant="caption" color="text.secondary">
                              ({restaurant.userRatingsTotal})
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                      {restaurant.priceLevel !== null && (
                        <Chip
                          label={PRICE_LABELS[restaurant.priceLevel] || `Level ${restaurant.priceLevel}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {restaurant.isOpenNow !== null && (
                        <Chip
                          label={restaurant.isOpenNow ? 'Open Now' : 'Closed'}
                          size="small"
                          color={restaurant.isOpenNow ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>

                    {restaurant.address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        <PlaceIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                        {restaurant.address}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelect(restaurant)}
                      >
                        Select
                      </Button>
                      {restaurant.googleMapsUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenIcon />}
                          href={restaurant.googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Maps
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
